import { useMemo, useState } from 'react';
import { useAdminI18n } from '@/lib/i18n/use-admin-i18n';
import type { PaginationState, SortingState } from '@tanstack/react-table';
import type { ServiceAccount } from '@cms/models';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { DynamicTable } from '@/components/custom/table/DynamicTable';
import type { DynamicTableColumnConfig, DynamicTableControllers } from '@/components/custom/table/dynamic-table-types';
import { SearchableSelect } from '@/components/custom/forms/SearchableSelect';
import { SearchFilter, StatusFilter } from '@/components/custom/filters';
import { STATUS_OPTIONS } from '@cms/utils';
import { saasApi } from '@/lib/api/saas.api';
import { extractApiErrorMessage } from '@/lib/api/extract-api-error-message';
import { useDeleteServiceAccount, useServiceAccounts } from '@/lib/api/service-accounts';
import {
  AdminTableRowActions,
  PagePrimaryActionButton,
} from '@/components/common/AdminTableRowActions';
import { ServiceAccountFormModal, type ServiceAccountFormModalMode } from './ServiceAccountFormModal';

type ServiceAccountStatusFilter = (typeof STATUS_OPTIONS)[number]['value'] | '';

interface ServiceAccountsTableProps {
  rows: ServiceAccount[];
  controllers?: DynamicTableControllers;
  rowOffset: number;
  onView: (item: ServiceAccount) => void;
  onEdit: (item: ServiceAccount) => void;
  onClone: (item: ServiceAccount) => void;
  onDelete: (item: ServiceAccount) => void;
  isDeleting: boolean;
  isLoading: boolean;
}

function ServiceAccountsTable(props: Readonly<ServiceAccountsTableProps>) {
  const { rows, controllers, rowOffset, onView, onEdit, onClone, onDelete, isDeleting, isLoading } = props;
  const { col, act, page, statusOptions, tableLoading } = useAdminI18n();

  const columns: DynamicTableColumnConfig<ServiceAccount>[] = useMemo(
    () => [
    {
      id: 'no',
      header: col('no'),
      columnType: 'number',
      cell: ({ index }) => rowOffset + index + 1,
      widthMode: 'fit',
      size: 64,
      meta: { className: 'w-16 text-muted-foreground' },
    },
    { id: 'name', accessorKey: 'name', header: col('name'), sortable: true, columnType: 'text' },
    { id: 'platform_code', accessorKey: 'platform_code', header: col('platform'), sortable: true, columnType: 'text' },
    { id: 'tenant_name', accessorKey: 'tenant_name', header: col('tenant'), columnType: 'text' },
    { id: 'username', accessorKey: 'username', header: col('username'), columnType: 'text' },
    {
      id: 'status',
      accessorKey: 'status',
      header: col('status'),
      sortable: true,
      columnType: 'badge',
      displayLabelOptions: statusOptions,
      widthMode: 'fit',
      size: 120,
      meta: { className: 'min-w-[120px]' },
    },
    {
      id: 'actions',
      header: col('actions'),
      columnType: 'actions',
      cell: ({ row }) => (
        <AdminTableRowActions
          act={act}
          onView={() => onView(row)}
          onEdit={() => onEdit(row)}
          onClone={() => onClone(row)}
          onDelete={() => onDelete(row)}
          isDeleting={isDeleting}
        />
      ),
      widthMode: 'fit',
      size: 300,
      minSize: 240,
      meta: { className: 'min-w-[240px] text-right' },
    },
    ],
    [act, col, isDeleting, onClone, onDelete, onEdit, onView, rowOffset, statusOptions],
  );

  return (
    <DynamicTable<ServiceAccount>
      features={{
        columns: true,
        rows: true,
        sorting: true,
        pagination: true,
        globalFilter: false,
        stripedRows: true,
        fontSize: 'xs',
        compactPadding: true,
      }}
      columns={columns}
      rows={{ data: rows, getRowId: row => row.id }}
      controllers={controllers}
      isLoading={isLoading}
      loadingMessage={tableLoading}
      emptyMessage={page('service_accounts', 'empty')}
    />
  );
}

export function ServiceAccountsPage() {
  const { page, statusOptions, allStatusesOption } = useAdminI18n();
  const query = useServiceAccounts();
  const deleteMutation = useDeleteServiceAccount();
  const [sorting, setSorting] = useState<SortingState>([{ id: 'name', desc: false }]);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 25 });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ServiceAccountStatusFilter>('');
  const [tenantFilter, setTenantFilter] = useState('');
  const [formModal, setFormModal] = useState<{
    open: boolean;
    mode: ServiceAccountFormModalMode;
    serviceAccount: ServiceAccount | null;
  }>({ open: false, mode: 'create', serviceAccount: null });
  const rows = query.data ?? [];
  const { data: tenants = [] } = useQuery({
    queryKey: ['tenants'],
    queryFn: () => saasApi.getTenants(),
  });
  const tenantOptions = useMemo(
    () =>
      tenants.reduce<Array<{ value: string; label: string }>>((acc, tenant) => {
        if (!tenant.id) return acc;
        acc.push({
          value: tenant.id,
          label: tenant.name ?? tenant.code ?? tenant.id,
        });
        return acc;
      }, []),
    [tenants],
  );

  const onDelete = async (item: ServiceAccount) => {
    const accountName = item.name?.trim() || item.id;
    const shouldDelete = globalThis.confirm(`Are you sure you want to delete AI Account "${accountName}"?`);
    if (!shouldDelete) {
      return;
    }
    try {
      await deleteMutation.mutateAsync(item.id);
      toast.success('AI Account deleted successfully.');
    } catch (error: unknown) {
      toast.error(extractApiErrorMessage(error));
    }
  };

  const filteredRows = useMemo(() => {
    let list = rows;
    const term = search.trim().toLowerCase();
    if (term) {
      list = list.filter(item => {
        const name = item.name?.toLowerCase() ?? '';
        const platformCode = item.platform_code?.toLowerCase() ?? '';
        const tenantName = item.tenant_name?.toLowerCase() ?? '';
        const username = item.username?.toLowerCase() ?? '';
        const notes = item.notes?.toLowerCase() ?? '';
        return (
          name.includes(term) ||
          platformCode.includes(term) ||
          tenantName.includes(term) ||
          username.includes(term) ||
          notes.includes(term)
        );
      });
    }
    if (statusFilter) {
      list = list.filter(item => item.status === statusFilter);
    }
    if (tenantFilter) {
      list = list.filter(item => item.tenant_id === tenantFilter);
    }
    return list;
  }, [rows, search, statusFilter, tenantFilter]);

  const sortedRows = useMemo(() => {
    if (!sorting.length) return filteredRows;
    const { id, desc } = sorting[0]!;
    return [...filteredRows].sort((a, b) => {
      if (id === 'status') {
        const cmp = (a.status ?? '').localeCompare(b.status ?? '');
        return desc ? -cmp : cmp;
      }
      const av =
        id === 'platform_code'
          ? a.platform_code ?? ''
          : id === 'tenant_name'
            ? a.tenant_name ?? ''
            : id === 'username'
              ? a.username ?? ''
              : a.name ?? '';
      const bv =
        id === 'platform_code'
          ? b.platform_code ?? ''
          : id === 'tenant_name'
            ? b.tenant_name ?? ''
            : id === 'username'
              ? b.username ?? ''
              : b.name ?? '';
      const cmp = String(av).localeCompare(String(bv));
      return desc ? -cmp : cmp;
    });
  }, [filteredRows, sorting]);

  const totalRecords = sortedRows.length;
  const pageSize = pagination.pageSize;
  const pageCount = Math.max(1, Math.ceil(Math.max(1, totalRecords) / pageSize));
  const safePageIndex = Math.min(pagination.pageIndex, pageCount - 1);

  const pageState: PaginationState = { pageIndex: safePageIndex, pageSize };

  const paginatedRows = useMemo(() => {
    const start = safePageIndex * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, safePageIndex, pageSize]);

  const controllers: DynamicTableControllers = {
    sort: {
      state: sorting,
      onSortingChange: updater => {
        setSorting(prev => (typeof updater === 'function' ? updater(prev) : updater));
        setPagination(prev => ({ ...prev, pageIndex: 0 }));
      },
    },
    pagination: {
      state: pageState,
      pageCount,
      rowCount: totalRecords,
      onPaginationChange: updater => {
        setPagination(prev => (typeof updater === 'function' ? updater(prev) : updater));
      },
    },
  };

  return (
    <div className="space-y-2 p-2">
      <div>
        <h1 className="text-3xl font-bold">{page('service_accounts', 'title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{page('service_accounts', 'subtitle')}</p>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div />
        <PagePrimaryActionButton
          onClick={() => setFormModal({ open: true, mode: 'create', serviceAccount: null })}
        >
          {page('service_accounts', 'new')}
        </PagePrimaryActionButton>
      </div>

      <div className="glass-card overflow-hidden">
        <div className="flex flex-wrap items-end gap-2 border-b border-border p-3">
          <SearchFilter
            id="service-accounts-search"
            label="Search"
            value={search}
            onChange={v => {
              setSearch(v);
              setPagination(prev => ({ ...prev, pageIndex: 0 }));
            }}
            placeholder="Search by name, platform, tenant, username, notes..."
            className="[&_input]:h-9 [&_input]:py-1.5 [&_input]:text-sm"
          />
          <StatusFilter
            id="service-accounts-status"
            label="Status"
            value={statusFilter}
            onChange={v => {
              setStatusFilter(v as ServiceAccountStatusFilter);
              setPagination(prev => ({ ...prev, pageIndex: 0 }));
            }}
            options={statusOptions}
            emptyOption={allStatusesOption}
            className="[&_button]:h-9 [&_input]:h-9 [&_input]:py-1.5 [&_input]:text-sm"
          />
          <div className="min-w-[220px]">
            <label htmlFor="service-accounts-tenant-filter" className="block text-sm text-muted-foreground">
              Tenant
            </label>
            <SearchableSelect
              id="service-accounts-tenant-filter"
              options={tenantOptions}
              value={tenantFilter}
              onChange={v => {
                setTenantFilter(v);
                setPagination(prev => ({ ...prev, pageIndex: 0 }));
              }}
              placeholder="Search tenant..."
              emptyOption={{ value: '', label: 'All tenants' }}
              className="mt-1 [&_input]:h-9 [&_input]:py-1.5 [&_input]:text-sm"
            />
          </div>
        </div>
        <ServiceAccountsTable
          rows={paginatedRows}
          controllers={controllers}
          rowOffset={pageState.pageIndex * pageState.pageSize}
          onView={item => setFormModal({ open: true, mode: 'view', serviceAccount: item })}
          onEdit={item => setFormModal({ open: true, mode: 'edit', serviceAccount: item })}
          onClone={item =>
            setFormModal({
              open: true,
              mode: 'create',
              serviceAccount: { ...item, id: '', name: `Copy of ${item.name}` },
            })
          }
          onDelete={item => void onDelete(item)}
          isDeleting={deleteMutation.isPending}
          isLoading={query.isLoading}
        />
      </div>

      <ServiceAccountFormModal
        open={formModal.open}
        onClose={() => setFormModal(prev => ({ ...prev, open: false, serviceAccount: null }))}
        mode={formModal.mode}
        serviceAccount={formModal.serviceAccount}
        onSuccess={() => setFormModal(prev => ({ ...prev, open: false, serviceAccount: null }))}
      />
    </div>
  );
}
